<script>
import {OrderStatus} from '@/enums/OrderStatus'
import OrdersLoading from './OrdersLoading.vue'
import OrdersFilter from './OrdersFilter.vue'
import OrdersList from './OrdersList.vue'
import Vue from 'vue'
import DocumentsTable from '@/components/documents/DocumentsTable.vue'

export default {
  components: {
    DocumentsTable,
    OrdersList,
    OrdersLoading,
    OrdersFilter,
  },
  props: {
    viewName: {type: String}
  },
  data() {
    return {
      openOrders: [],
      totalOpenOrders: 0,
      totalFinishedOrders: 0,
      totalErrorOrders: 0,
      openOrdersListKey: 1,
      openOrdersTableKey: 0,
      finishedOrders: [],
      errorOrders: [],
      openOrdersReady: false, // indicates, if data is loaded
      finishedOrdersReady: false, // indicates, if data is loaded
      errorOrdersReady: false, // indicates, if data is loaded
      orderUpdated: false,
      updatedOrderNumber: '',
      submitError: false,
      validationTableHeight: 550,
      savedFilterRules: {
        openOrders: {
          filterDate: true,
          dateFrom: new Date(new Date() - 7 * 86400000)
          .toISOString()
          .substr(0, 10),
          dateTo: new Date().toISOString().substr(0, 10)
        },
        finishedOrders: {
          filterDate: true,
          dateFrom: new Date(new Date() - 7 * 86400000)
          .toISOString()
          .substr(0, 10),
          dateTo: new Date().toISOString().substr(0, 10)
        },
        errorOrders: {
          filterDate: true,
          dateFrom: new Date(new Date() - 7 * 86400000)
          .toISOString()
          .substr(0, 10),
          dateTo: new Date().toISOString().substr(0, 10)
        }
      },
      pollingUpdateOrders: [],
      pollingUpdateOrdersInterval: 2000,
      pollingUpdateOrdersMaxRetrys: 10
    }
  },
  computed: {
    dataReady: function () {
      return (
        this.openOrdersReady &&
        this.finishedOrdersReady &&
        this.errorOrdersReady
      )
    }
  },
  watch: {
    // watch openOrders to update the orders when they are in open order pending-status (mostly for new Orders)
    openOrders(ordersNew) {
      let ordersPending = ordersNew
      .filter(order => order.orderStatus <= OrderStatus.pending)
      .reduce((acc, value) => {
        acc[value.orderId] = value.orderStatus

        return acc
      }, {})
      let orderIds = Object.keys(ordersPending)
      if (orderIds.length > 0) {
        this.updateOrders({
          orderIds: orderIds,
          source: 'Watcher'
        })
      }
    },
    'savedFilterRules.openOrders': {
      handler() {
        this.fetchOrder({
          orders: 'openOrders',
          orderReadyState: 'openOrdersReady',
          orderStatusMin: OrderStatus.open,
          orderStatusMax: OrderStatus.paymentRefused,
          filterRules: this.savedFilterRules.openOrders,
          totalOrders: 'totalOpenOrders'
        })
      },
      deep: true
    },
    'savedFilterRules.finishedOrders': {
      handler() {
        this.fetchOrder({
          orders: 'finishedOrders',
          orderReadyState: 'finishedOrdersReady',
          orderStatusMin: OrderStatus.paid,
          orderStatusMax: OrderStatus.cancelled,
          filterRules: this.savedFilterRules.finishedOrders,
          totalOrders: 'totalFinishedOrders'
        })
      },
      deep: true
    },
    'savedFilterRules.errorOrders': {
      handler() {
        this.fetchOrder({
          orders: 'errorOrders',
          orderReadyState: 'errorOrdersReady',
          orderStatusMin: OrderStatus.error,
          orderStatusMax: OrderStatus.error,
          filterRules: this.savedFilterRules.errorOrders,
          totalOrders: 'totalErrorOrders'
        })
      },
      deep: true
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  created: function () {

    this.fetchOrders()
  },
  methods: {
    sortOrders: function (ordersList) {
      return ordersList.sort(this.sortByDate)
    },
    sortByDate(a, b) {
      var keyA = new Date(a.orderCreateDateTime),
        keyB = new Date(b.orderCreateDateTime)
      if (keyA < keyB) return 1
      if (keyA > keyB) return -1
      return 0
    },
    fetchOrders: function () {
      this.openOrdersReady = false
      this.finishedOrdersReady = false
      this.errorOrdersReady = false
      // get all open orders
      this.fetchOrder({
        orders: 'openOrders',
        orderReadyState: 'openOrdersReady',
        orderStatusMin: OrderStatus.open,
        orderStatusMax: OrderStatus.paymentRefused,
        filterRules: this.savedFilterRules.openOrders,
        totalOrders: 'totalOpenOrders'
      })
      this.fetchOrder({
        orders: 'finishedOrders',
        orderReadyState: 'finishedOrdersReady',
        orderStatusMin: OrderStatus.paid,
        orderStatusMax: OrderStatus.cancelled,
        filterRules: this.savedFilterRules.finishedOrders,
        totalOrders: 'totalFinishedOrders'
      })
      this.fetchOrder({
        orders: 'errorOrders',
        orderReadyState: 'errorOrdersReady',
        orderStatusMin: OrderStatus.error,
        orderStatusMax: OrderStatus.error,
        filterRules: this.savedFilterRules.errorOrders,
        totalOrders: 'totalErrorOrders'
      })
    },
    fetchOrder: function (args) {
      this[args.orderReadyState] = false
      this.$ordersService
      .getOrders({
        min: args.orderStatusMin,
        max: args.orderStatusMax,
        filterRules: args.filterRules
      })
      .then(ordersResponse => {
        this[args.orders] = this.sortOrders(ordersResponse.ordersList)
        this[args.totalOrders] = ordersResponse.totalOrders
      })
      .catch(e => {
        this[args.orders] = []
        this[args.totalOrders] = e.response.data.totalOrders
      })
      .finally(() => {
        this[args.orderReadyState] = true
      })
    },
    updateOrders: async function (event, lastOpenOrder = null, retryCount = 1) {
      if (event === null) return
      let orderIds = [...new Set(event.orderIds)]
      let source = event.source || 'DataTable'
      retryCount++
      for (let i = 0; i < orderIds.length; i++) {
        // get index of order
        let openOrdersIndex = this.openOrders.findIndex(
          order => order.orderId == orderIds[i]
        )
        // get the actual not refreshed order
        // stringify to check if something has changed (documentCount, orderStatus, pageCount...)
        let openOrder =
          lastOpenOrder || JSON.stringify(this.openOrders[openOrdersIndex])
        await this.$ordersService
        .getOrder(orderIds[i])
        .then(orderResponse => {
          // check if documentCount has changed (one or more documents has been deleted)
          // check if status has changed
          if (openOrder !== JSON.stringify(orderResponse)) {
            // update order
            if (source === 'Watcher') {
              // if the updateOrders-Function is fired by the Watcher, force Vue to update the openOrders-array, so the watcher can read the changes
              // otherwise the watcher does not get the changes and do nothing
              Vue.set(this.openOrders, openOrdersIndex, orderResponse)
              // stop polling for all timeouts to prevent multiple timeouts
              // because the watcher will fire updateOrders after an order has been updated
              this.stopPolling()
            } else {
              // update the array directly, so the watcher don't fires the event twice
              this.openOrders[openOrdersIndex] = orderResponse
            }
            this.updatedOrderNumber = orderResponse.orderNumber
            this.orderUpdated = true
            this.reloadDataTable(source) // force reload datatable
          } else {
            // restart function with delay
            // stop after maxRetrys is reached
            if (retryCount <= this.pollingUpdateOrdersMaxRetrys) {
              this.pollingUpdateOrders[orderIds[i]] = setTimeout(
                this.updateOrders,
                this.pollingUpdateOrdersInterval + retryCount * 1000,
                {
                  orderIds: [orderIds[i]],
                  source: source
                },
                openOrder,
                retryCount
              )
            }
          }
        })
        .catch(e => {
          if (e.response.status === 404) {
            delete this.openOrders.splice(
              this.openOrders.findIndex(
                order => order.orderId == orderIds[i]
              ),
              1
            ) // delete empty order from array
          }
        })
      }
    },
    reloadDataTable(source) {
      this.openOrdersListKey++
      // reload documentValidationDataTable
      if (source === 'Dialog' || source === 'Watcher') this.openOrdersTableKey++
    },
    stopPolling() {
      this.pollingUpdateOrders.forEach(element => {
        clearInterval(element)
        element = null
      })
      this.pollingUpdateOrders = []
    }
  }
}
</script>
<template>
  <div>
    <v-snackbar v-model="orderUpdated" top color="primary" :timeout="3000">
      <span
        v-html="$t('component.orders.wrapper.order.updated.snackbar')
            .replace('{{orderNumber}}', updatedOrderNumber)">
      </span>
      <v-btn color="white" icon @click="orderUpdated = false">
        <v-icon size="20">mdi-close</v-icon>
      </v-btn>
    </v-snackbar>
    <v-card>
      <v-tabs show-arrows>
        <v-tab name="open-orders">
          <v-icon>mdi-folder-open-outline</v-icon>
          <span class="pl-1">{{ $t('global.open') }}</span>
        </v-tab>
        <v-tab name="closed-orders">
          <v-icon>mdi-folder-outline</v-icon>
          <span class="pl-1">{{ $t('global.closed') }}</span>
        </v-tab>
        <v-tab
          v-if="errorOrders.length > 0"
          name="error-orders">
          <v-icon>mdi-alert-circle-outline</v-icon>
          <span class="pl-1">{{ $t('component.orders.wrapper.error.orders.tab') }}</span>
        </v-tab>
        <v-tab-item>
          <v-divider/>
          <OrdersLoading :is-loading="!dataReady"/>
          <OrdersFilter
            :defaults="savedFilterRules.openOrders"
            :saved-filter-rules.sync="savedFilterRules.openOrders"
            :filtered-orders-count="openOrders.length"
            :total-orders-count="totalOpenOrders"
            :class="!dataReady ? 'd-none' : ''"
            hide-select-document-status />
          <v-divider v-if="dataReady"/>
          <OrdersList
            v-if="dataReady"
            :key="openOrdersListKey"
            :class="viewName === 'list' ? '' : 'd-none'"
            status="open"
            :orders="openOrders"
            :fetch-orders="fetchOrders"
            :total-orders="totalOpenOrders"
            @update:updated="updateOrders($event)"/>
          <DocumentsTable
            v-if="dataReady"
            :key="openOrdersTableKey"
            :class="viewName === 'documents' ? '' : 'd-none'"
            :order="openOrders"
            :can-delete="true"
            :height="validationTableHeight"
            :fetch-orders="fetchOrders"
            :show-order-number="true"
            :filter-rules="savedFilterRules.openOrders"
            @update:updated="updateOrders($event)"/>
        </v-tab-item>
        <v-tab-item>
          <v-divider/>
          <OrdersLoading :is-loading="!dataReady"/>
          <OrdersFilter
            :defaults="savedFilterRules.finishedOrders"
            :saved-filter-rules.sync="savedFilterRules.finishedOrders"
            :filtered-orders-count="finishedOrders.length"
            :total-orders-count="totalFinishedOrders"
            :class="!dataReady ? 'd-none' : ''"
            hide-select-document-status
            hide-select-order-status/>
          <v-divider v-if="dataReady"/>
          <OrdersList
            v-if="dataReady"
            :class="viewName === 'list' ? '' : 'd-none'"
            status="closed"
            :orders="finishedOrders"
            :fetch-orders="fetchOrders"
            :total-orders="totalFinishedOrders"
            @update:updated="updateOrders($event)"/>
          <DocumentsTable
            v-if="dataReady"
            :class="viewName === 'documents' ? '' : 'd-none'"
            :order="finishedOrders"
            :can-delete="false"
            :height="validationTableHeight"
            :fetch-orders="fetchOrders"
            :show-order-number="true"
            @update:updated="updateOrders($event)"/>
        </v-tab-item>
        <v-tab-item>
          <v-divider/>
          <OrdersLoading :is-loading="!dataReady"/>
          <OrdersFilter
            :defaults="savedFilterRules.errorOrders"
            :saved-filter-rules.sync="savedFilterRules.errorOrders"
            :filtered-orders-count="errorOrders.length"
            :total-orders-count="totalErrorOrders"
            :class="!dataReady ? 'd-none' : ''"
            hide-select-document-status
            hide-select-order-status/>
          <v-divider v-if="dataReady"/>
          <OrdersList
            v-if="dataReady"
            :class="viewName === 'list' ? '' : 'd-none'"
            status="error"
            :orders="errorOrders"
            :fetch-orders="fetchOrders"
            :total-orders="totalErrorOrders"
            @update:updated="updateOrders($event)"/>
          <DocumentsTable
            v-if="dataReady"
            :class="viewName === 'documents' ? '' : 'd-none'"
            :order="errorOrders"
            :can-delete="false"
            :height="validationTableHeight"
            :fetch-orders="fetchOrders"
            :show-order-number="true"
            @update:updated="updateOrders($event)"/>
        </v-tab-item>
      </v-tabs>
    </v-card>
  </div>
</template>
